// client/src/coms/views/canvas_parts/party/gifEncoder.js
// A small GIF89a encoder for flipbook exports. Party drawings use a fixed palette, so every frame maps to
// one global colour table (nearest colour per pixel) and is LZW-compressed. Loops forever.

function nearestIndex(palette, r, g, b, cache) {
  const key = (r << 16) | (g << 8) | b;
  const hit = cache.get(key);
  if (hit !== undefined) return hit;
  let best = 0;
  let bestDistance = Infinity;
  for (let i = 0; i < palette.length; i += 1) {
    const [pr, pg, pb] = palette[i];
    const d = (pr - r) ** 2 + (pg - g) ** 2 + (pb - b) ** 2;
    if (d < bestDistance) {
      bestDistance = d;
      best = i;
    }
  }
  cache.set(key, best);
  return best;
}

function lzwEncode(indices, minCodeSize) {
  const clearCode = 1 << minCodeSize;
  const endCode = clearCode + 1;
  const out = [];
  let buffer = 0;
  let bits = 0;
  let codeSize = minCodeSize + 1;
  let dictionary = new Map();
  let next = endCode + 1;
  const write = (code) => {
    buffer |= code << bits;
    bits += codeSize;
    while (bits >= 8) {
      out.push(buffer & 255);
      buffer >>= 8;
      bits -= 8;
    }
  };
  write(clearCode);
  let prefix = indices[0];
  for (let i = 1; i < indices.length; i += 1) {
    const value = indices[i];
    const key = prefix * 4096 + value;
    const found = dictionary.get(key);
    if (found !== undefined) {
      prefix = found;
      continue;
    }
    write(prefix);
    if (next < 4096) {
      dictionary.set(key, next);
      next += 1;
      if (next > 1 << codeSize && codeSize < 12) codeSize += 1;
    } else {
      write(clearCode);
      dictionary = new Map();
      next = endCode + 1;
      codeSize = minCodeSize + 1;
    }
    prefix = value;
  }
  write(prefix);
  write(endCode);
  if (bits > 0) out.push(buffer & 255);
  return out;
}

// frames: array of ImageData of identical size; palette: array of '#rrggbb'. Returns a Blob.
export function encodeGif(frames, { width, height, delayMs = 160, palette }) {
  const colours = palette.slice(0, 256).map((hex) => {
    const v = parseInt(hex.slice(1), 16);
    return [(v >> 16) & 255, (v >> 8) & 255, v & 255];
  });
  let tableBits = 1;
  while (1 << tableBits < colours.length) tableBits += 1;
  while (colours.length < 1 << tableBits) colours.push([0, 0, 0]);
  const bytes = [];
  const push16 = (n) => bytes.push(n & 255, (n >> 8) & 255);
  bytes.push(...'GIF89a'.split('').map((c) => c.charCodeAt(0)));
  push16(width);
  push16(height);
  bytes.push(0x80 | ((tableBits - 1) << 4) | (tableBits - 1), 0, 0);
  colours.forEach(([r, g, b]) => bytes.push(r, g, b));
  // NETSCAPE2.0 loop extension: loop forever.
  bytes.push(0x21, 0xff, 0x0b, ...'NETSCAPE2.0'.split('').map((c) => c.charCodeAt(0)), 0x03, 0x01, 0x00, 0x00, 0x00);
  const cache = new Map();
  const delay = Math.max(2, Math.round(delayMs / 10));
  frames.forEach((frame) => {
    bytes.push(0x21, 0xf9, 0x04, 0x00);
    push16(delay);
    bytes.push(0x00, 0x00);
    bytes.push(0x2c);
    push16(0);
    push16(0);
    push16(width);
    push16(height);
    bytes.push(0x00);
    const indices = new Array(width * height);
    const data = frame.data;
    for (let i = 0, p = 0; i < indices.length; i += 1, p += 4) {
      indices[i] = nearestIndex(colours, data[p], data[p + 1], data[p + 2], cache);
    }
    const minCodeSize = Math.max(2, tableBits);
    bytes.push(minCodeSize);
    const encoded = lzwEncode(indices, minCodeSize);
    for (let i = 0; i < encoded.length; i += 255) {
      const block = encoded.slice(i, i + 255);
      bytes.push(block.length, ...block);
    }
    bytes.push(0x00);
  });
  bytes.push(0x3b);
  return new Blob([new Uint8Array(bytes)], { type: 'image/gif' });
}
